/* ***** BEGIN LICENSE BLOCK *****
 * Distributed under the BSD license:
 *
 * Copyright (c) 2012, Ajax.org B.V.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *     * Redistributions of source code must retain the above copyright
 *       notice, this list of conditions and the following disclaimer.
 *     * Redistributions in binary form must reproduce the above copyright
 *       notice, this list of conditions and the following disclaimer in the
 *       documentation and/or other materials provided with the distribution.
 *     * Neither the name of Ajax.org B.V. nor the
 *       names of its contributors may be used to endorse or promote products
 *       derived from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL AJAX.ORG B.V. BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 * SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 * ***** END LICENSE BLOCK ***** */

import "ace-builds/src-noconflict/mode-markdown";
import "ace-builds/src-noconflict/ext-language_tools";

export default function templatemode(ace) {
    ace.define(
        "ace/mode/template",
        ["require", "exports", "module", "ace/lib/oop", "ace/lib/lang", "ace/mode/behaviour", "ace/mode/markdown"],
        function (req, exp, module) {
            "use strict";

            let oop = req("../lib/oop");
            let lang = req("../lib/lang");
            let Behaviour = req("./behaviour").Behaviour;
            let TokenIterator = req("../token_iterator").TokenIterator;

            let MarkdownMode = req("./markdown").Mode;
            let MarkdownHighlightRules = req("./markdown_highlight_rules").MarkdownHighlightRules;

            function pop2(currentState, stack) {
                stack.splice(0, 3);
                return stack.shift() || "start";
            }

            let TemplateHighlightRules = function () {
                MarkdownHighlightRules.call(this);

                let hbs = {
                    regex: "(?={{)",
                    push: "handlebars",
                };
                for (let key in this.$rules) {
                    this.$rules[key].unshift(hbs);
                }
                this.$rules.handlebars = [
                    {
                        token: "comment.start",
                        regex: "{{!--",
                        push: [
                            {
                                token: "comment.end",
                                regex: "--}}",
                                next: pop2,
                            },
                            {
                                defaultToken: "comment",
                            },
                        ],
                    },
                    {
                        token: "comment.start",
                        regex: "{{!",
                        push: [
                            {
                                token: "comment.end",
                                regex: "}}",
                                next: pop2,
                            },
                            {
                                defaultToken: "comment",
                            },
                        ],
                    },
                    {
                        token: "support.function.completion", // unescaped variable
                        regex: "{{{",
                        push: [
                            {
                                token: "support.function",
                                regex: "}}}",
                                next: pop2,
                            },
                            {
                                token: "variable.parameter.completion",
                                regex: "[a-zA-Z_$][a-zA-Z0-9_$]*",
                            },
                        ],
                    },
                    {
                        token: "storage.type.start.completion", // begin section
                        regex: "{{[#\\^/&]?",
                        push: [
                            {
                                token: "storage.type.end",
                                regex: "}}",
                                next: pop2,
                            },
                            {
                                token: "variable.parameter.completion",
                                regex: "[a-zA-Z_$][a-zA-Z0-9_$]*",
                            },
                        ],
                    },
                ];

                this.normalizeRules();
            };

            oop.inherits(TemplateHighlightRules, MarkdownHighlightRules);

            let getWrapped = function(selection, selected, opening, closing) {
                let rowDiff = selection.end.row - selection.start.row;
                return {
                    text: opening + selected + closing,
                    selection: [
                        0,
                        selection.start.column + 1,
                        rowDiff,
                        selection.end.column + (rowDiff ? 0 : 1)
                    ]
                };
            };

            let TemplateBehaviour = function (options) {
                this.add("braces", "insertion", function (state, action, editor, session, text) {
                    let cursor = editor.getCursorPosition();
                    let line = session.doc.getLine(cursor.row);
                    if (text === "{") {
                        let selection = editor.getSelectionRange();
                        let selected = session.doc.getTextRange(selection);
                        if (
                            selected !== "" &&
                            selected !== "{" &&
                            editor.getWrapBehavioursEnabled()
                        ) {
                            return getWrapped(selection, selected, "{{", "}}");
                        } else if (TemplateBehaviour.isSaneInsertion(editor, session)) {
                            let left = line.slice(0, cursor.column);
                            if (left === "{" || /[^{]\{$/.test(left)) {
                                setTimeout(() => {
                                    editor.commands.byName.startAutocomplete.exec(editor);
                                });
                                return {
                                    text: "{}}",
                                    selection: [1, 1],
                                };
                            } else if (left === "{{" || /\{\{$/.test(left)) {
                                return {
                                    text: "{}",
                                    selection: [1, 1]
                                }
                            }
                        }
                    } else if (text === "}") {
                        let rightChar = line.substring(cursor.column, cursor.column + 1);
                        if (rightChar === "}") {
                            let matching = session.$findOpeningBracket("}", {
                                column: cursor.column + 1,
                                row: cursor.row,
                            });
                            if (
                                matching !== null
                            ) {
                                return {
                                    text: "",
                                    selection: [1, 1],
                                };
                            }
                        }
                    }
                });

                this.add("braces", "deletion", function (state, action, editor, session, range) {
                    let selected = session.doc.getTextRange(range);
                    if (!range.isMultiLine() && selected === "{") {
                        let line = session.doc.getLine(range.start.row);
                        let rightChar = line.substring(range.end.column, range.end.column + 1);
                        if (rightChar === "}") {
                            range.end.column++;
                            return range;
                        }
                    }
                });

                this.add("section", "insertion", function (state, action, editor, session, text) {
                    let cursor = editor.getCursorPosition();
                    let line = session.doc.getLine(cursor.row);
                    if (text === "/") {
                        let selection = editor.getSelectionRange();
                        let selected = session.doc.getTextRange(selection);
                        if (selected === "") {
                            let left = line.slice(0, cursor.column);
                            if (/\{\{$/.test(left)) {
                                let iterator = new TokenIterator(session, cursor.row, cursor.column);
                                let token = iterator.stepBackward();
                                let end = false;
                                let name = "";

                                while (token && token.type !== "storage.type.start.completion") {
                                    if (token.type === "storage.type.end") {
                                        end = true;
                                    } else if (end) {
                                        name = token.value + name;
                                    }

                                    token = iterator.stepBackward();

                                    // If it's not the section close, skip that token and continue looking
                                    if (token?.type === "storage.type.start.completion" && token.value !== "{{#") {
                                        token = iterator.stepBackward();
                                        end = false;
                                        name = "";
                                    }
                                }
                                if (token && name) {
                                    return {
                                        text: "/" + name,
                                        selection: [name.length + 1, name.length + 1]
                                    }
                                }
                            }
                        }
                    }
                });
            };

            TemplateBehaviour.isSaneInsertion = function (editor, session) {
                return true;
            };

            TemplateBehaviour.$matchTokenType = function (token, types) {
                return types.indexOf(token.type || token) > -1;
            };

            oop.inherits(TemplateBehaviour, Behaviour);

            let Mode = function () {
                this.HighlightRules = TemplateHighlightRules;
                this.$behaviour = new TemplateBehaviour();
            };
            oop.inherits(Mode, MarkdownMode);

            exp.TemplateHighlightRules = TemplateHighlightRules;
            exp.Mode = Mode;
        }
    );
}
